/*
 * Host-side undo actions.
 *
 * miniPaint's undo system takes command objects: `State.do_action(action)` awaits
 * `action.do()`, pushes it on `State.action_history`, and `State.undo()` calls
 * `action.undo()`. Verified in the vendored source (src/js/core/base-state.js):
 * the call site is fully duck-typed — no `instanceof`, no prototype check — it only
 * reads `action_id`, `action_description`, and calls `do()`, `undo()`, `free()`.
 *
 * That matters because miniPaint does NOT expose `window.app`, so its own
 * `app.Actions.*` classes are unreachable from this page. Defining our own command
 * objects here and handing them to miniPaint's history is what makes an agent edit
 * reversible by a human pressing Ctrl+Z — without forking miniPaint.
 *
 * Contract for subclasses:
 *   - `do()` must be re-runnable: redo calls it again after an undo.
 *   - `undo()` must restore exactly what `do()` changed, nothing more.
 *   - capture the old value inside `do()`, not in the constructor — the document may
 *     have changed between construction and execution.
 *   - throwing from `do()` aborts cleanly: miniPaint returns { status: 'aborted' }
 *     and does not add the action to history.
 */

export class HostAction {
  /**
   * @param {string} actionId short stable id, e.g. 'update_layer'
   * @param {string} description shown in miniPaint's own history UI
   */
  constructor (actionId, description) {
    this.action_id = actionId
    this.action_description = description
    this.is_done = false
    // Read by miniPaint's memory-pressure logic; ours hold no bitmap data.
    this.memory_estimate = 0
    this.database_estimate = 0
  }

  async do () { this.is_done = true }
  async undo () { this.is_done = false }
  free () {}
}

/**
 * Update fields on an existing layer, reversibly.
 *
 * This is the host-side equivalent of miniPaint's own Update_layer_action, which we
 * cannot reach. It captures the previous values of exactly the keys it writes.
 */
export class UpdateLayerAction extends HostAction {
  /**
   * @param {Window} win the miniPaint window
   * @param {number|string} layerId
   * @param {object} settings fields to write onto the layer
   * @param {string} [description]
   */
  constructor (win, layerId, settings, description = 'Update layer') {
    super('update_layer', description)
    this.win = win
    this.layerId = layerId
    this.settings = settings
    this.previous = null
  }

  async do () {
    const layer = this.win.Layers.get_layer(this.layerId)
    if (!layer) throw new Error(`Layer ${this.layerId} no longer exists`)

    // Capture on every run so redo-after-further-edits stays correct.
    this.previous = {}
    for (const key of Object.keys(this.settings)) {
      this.previous[key] = layer[key]
      layer[key] = this.settings[key]
    }
    if (layer.type === 'text') layer._needs_update_data = true
    this.win.AppConfig.need_render = true
    this.win.Layers.render()
    this.is_done = true
  }

  async undo () {
    const layer = this.win.Layers.get_layer(this.layerId)
    if (layer && this.previous) {
      for (const key of Object.keys(this.previous)) layer[key] = this.previous[key]
      if (layer.type === 'text') layer._needs_update_data = true
      this.win.AppConfig.need_render = true
      this.win.Layers.render()
    }
    this.is_done = false
  }
}

/**
 * Run several host actions as one undo step, so an agent's compound operation is
 * reversed by a single Ctrl+Z rather than N of them.
 */
export class BundleHostAction extends HostAction {
  constructor (actions, description = 'Agent operation', actionId = 'bundle') {
    super(actionId, description)
    this.actions = actions
  }

  async do () {
    const done = []
    try {
      for (const action of this.actions) {
        await action.do()
        done.push(action)
      }
    } catch (err) {
      // Roll back the part that succeeded so a failed bundle leaves no half-state.
      for (const action of done.reverse()) {
        try { await action.undo() } catch { /* best effort */ }
      }
      throw err
    }
    this.is_done = true
  }

  async undo () {
    for (const action of [...this.actions].reverse()) await action.undo()
    this.is_done = false
  }

  free () {
    for (const action of this.actions) {
      if (typeof action.free === 'function') action.free()
    }
  }
}

/**
 * Move a layer through the z-order in a single undo step.
 *
 * miniPaint's own `Layers.move(id, direction)` only swaps with the neighbour above
 * or below, so "bring to front" over five layers would be five history entries and
 * five undos. This rewrites the whole `order` sequence at once and restores every
 * previous value on undo.
 */
export class SetLayerOrderAction extends HostAction {
  /**
   * @param {Window} win
   * @param {number|string} layerId
   * @param {'front'|'back'|'forward'|'backward'} position
   */
  constructor (win, layerId, position) {
    super('reorder_layer', 'Reorder Layer')
    this.win = win
    this.layerId = layerId
    this.position = position
    this.previous = null
  }

  async do () {
    const layers = this.win.Layers.get_layers()
    this.previous = layers.map((l) => ({ layer: l, order: l.order }))

    const sorted = [...layers].sort((a, b) => a.order - b.order)
    const index = sorted.findIndex((l) => String(l.id) === String(this.layerId))
    if (index === -1) throw new Error(`Layer ${this.layerId} no longer exists`)

    const [layer] = sorted.splice(index, 1)
    let target
    switch (this.position) {
      case 'front': target = sorted.length; break
      case 'back': target = 0; break
      case 'forward': target = Math.min(sorted.length, index + 1); break
      default: target = Math.max(0, index - 1)
    }
    sorted.splice(target, 0, layer)
    sorted.forEach((l, i) => { l.order = i + 1 })

    this._repaint()
    this.is_done = true
  }

  async undo () {
    if (this.previous) for (const { layer, order } of this.previous) layer.order = order
    this._repaint()
    this.is_done = false
  }

  _repaint () {
    this.win.AppConfig.need_render = true
    this.win.Layers.render()
    // Keep miniPaint's own layers panel in step with the new order.
    const gui = this.win.Layers.Base_gui
    if (gui && gui.GUI_layers && typeof gui.GUI_layers.render_layers === 'function') {
      gui.GUI_layers.render_layers()
    }
  }
}
